import { makeid } from 'utils/helpers/makeid';
import { DateFormatter } from 'infrastructure/functions';

import ScriptWrapper from './script-wrapper';
import { addField, formatList, hasField, mapValuesToText } from '../helper';

import type { BlurbContext, ITemplateProps } from '../types';
import type {
  ExtractStepValues,
  TFormTemplateItem,
} from '../../../molecules/base-form-template/types';

export const createTransitionTemplate = (data: ITemplateProps) => {
  const { userFullname } = data;

  return [
    {
      key: makeid(10),
      name: 'greetingComplianceHipaaVerification',
      type: 'checkbox',
      title: <p>Greeting & Compliance / HIPAA Verification</p>,
      description: (
        <ScriptWrapper>
          "Hello, this is {userFullname} with Tellihealth. For quality and
          compliance purposes, this call may be recorded. To protect your
          privacy, can you please confirm your full name and date of birth?" If
          caregiver present: "Do I have the patient's consent to speak with you
          today?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Identity verified',
          value: 'patientIdentityVerified',
        },
        {
          key: makeid(10),
          label: 'Caregiver present → Consent confirmed',
          value: 'caregiverPresentConsentConfirmed',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "Are you the patient's caregiver? Do I have the patient's
                  consent to speak with you today?"
                </ScriptWrapper>
              ),
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Recorded line',
          value: 'recordedLineNotificationProvided',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'readingAdherenceBarrierCheck',
      type: 'radio',
      title: <p>Reading Adherence & Barrier Check</p>,
      description: (
        <ScriptWrapper>
          "Before we discuss the reason for your call, I’d like to check in on
          your readings. Have you been able to take and send your readings most
          days, or has anything been getting in the way?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Consistent readings',
          value: 'consistentReadings',
        },
        {
          key: makeid(10),
          label: 'Inconsistent readings',
          value: 'inconsistentReadings',
        },
        {
          key: makeid(10),
          label: 'No readings',
          value: 'noReadings',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "Thank you for letting me know — I’ll document that so we can
                  follow up appropriately."
                </ScriptWrapper>
              ),
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Other',
          value: 'other',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'textarea',
              name: 'readingAdherenceCheckOther',
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Barriers identified',
          value: 'barriersIdentified',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "Thank you for letting me know — I’ll document that so we can
                  support you."
                </ScriptWrapper>
              ),
            },
            {
              key: makeid(10),
              name: 'barriersList',
              type: 'checkbox',
              title: <p>Barriers</p>,
              description: '',
              required: false,
              closable: true,
              options: [
                {
                  key: makeid(10),
                  label: 'Device',
                  value: 'device',
                },
                {
                  key: makeid(10),
                  label: 'Supplies',
                  value: 'supplies',
                },
                {
                  key: makeid(10),
                  label: 'Schedule',
                  value: 'schedule',
                },
                {
                  key: makeid(10),
                  label: 'Education',
                  value: 'education',
                },
                {
                  key: makeid(10),
                  label: 'Connectivity',
                  value: 'connectivity',
                },
                {
                  key: makeid(10),
                  label: 'Power/charging',
                  value: 'powerCharging',
                },
                {
                  key: makeid(10),
                  label: 'Other',
                  value: 'other',
                  hideSubitemsIfParentNotSelected: true,
                  subItems: [
                    {
                      key: makeid(10),
                      type: 'textarea',
                      name: 'barriersListOther',
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
    {
      key: makeid(10),
      name: 'addressImmediateNeed',
      type: 'radio',
      title: <p>Address Immediate Need</p>,
      description: (
        <ScriptWrapper>
          "How can I help you today?" (Resolve issue.)
          <br />
          "Just to confirm, today we [summarize action taken]."
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Resolved',
          value: 'resolved',
        },
        {
          key: makeid(10),
          label: 'Support provided',
          value: 'supportProvided',
        },
        {
          key: makeid(10),
          label: 'Escalated',
          value: 'escalated',
        },
      ],
      items: [
        {
          key: makeid(10),
          description: 'Resolve issue',
          name: 'addressImmediateNeedIssue',
          type: 'textarea',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'insuranceVerificationOnFile',
      type: 'radio',
      title: <p>Insurance Verification (On File Only)</p>,
      description: (
        <ScriptWrapper>
          "I want to confirm the type of insurance we currently have on file for
          you."
          <br />
          "What type of insurance do you have listed with your clinic: Medicare,
          Medicare Advantage, or Commercial?"
          <br />
          If Medicare Advantage: "Which plan is listed?"
          <br />
          "Thank you — I'll confirm what's documented in your chart."
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Medicare',
          value: 'medicare',
        },
        {
          key: makeid(10),
          label: 'Medicare Advantage (plan selected)',
          value: 'medicareAdvantage',
        },
        {
          key: makeid(10),
          label: 'Commercial',
          value: 'commercial',
        },
        {
          key: makeid(10),
          label: 'Insurance confirmed in chart',
          value: 'insuranceConfirmedInChart',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'cellPhoneVerification',
      type: 'radio',
      title: <p>Cell Phone Verification</p>,
      description: (
        <ScriptWrapper>
          "Before we continue, I want to confirm the best cell phone number to
          reach you. Is [number on file] still correct?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Cell phone number confirmed',
          value: 'cellPhoneNumberConfirmed',
        },
        {
          key: makeid(10),
          label: 'Cell phone number updated',
          value: 'cellPhoneNumberUpdated',
        },
        {
          key: makeid(10),
          label: 'Patient reports no cell phone',
          value: 'patientReportsNoCellPhone',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'textingEnrollment',
      type: 'radio',
      title: <p>Texting Enrollment</p>,
      description: (
        <ScriptWrapper>
          "If you have a cell phone, we also offer text messages for reminders
          and updates related to your RPM care."
          <br />
          "Would you like to receive text messages from us?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Already enrolled',
          value: 'alreadyEnrolled',
        },
        {
          key: makeid(10),
          label: 'Offered and accepted → Enrolled',
          value: 'offeredAndAcceptedEnrolled',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "Great — I'll get you enrolled. You can opt out at any time."
                </ScriptWrapper>
              ),
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Offered and declined',
          value: 'offeredAndDeclined',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "No problem — that's completely optional."
                </ScriptWrapper>
              ),
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Not eligible — no cell phone',
          value: 'notEligibleNoCellPhone',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'monthlyInteractionRequirement',
      type: 'checkbox',
      title: <p>Monthly Interaction Requirement + Next Monthly Check-In</p>,
      description: (
        <ScriptWrapper>
          "As part of the program, a monthly phone call with our team is
          required."
          <br />
          "These calls help us check in, answer questions, and make sure
          everything continues to work smoothly."
          <br />
          "If your weight changes outside of your provider's recommended range,
          your clinic may reach out — often with a call from a nurse — to decide
          if any follow-up is needed."
          <br />
          "Would you like to schedule your first monthly check-in now, or should
          we reach out closer to when it's due?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Requirement reviewed',
          value: 'requirementReviewed',
        },
        {
          key: makeid(10),
          label: 'Value explained',
          value: 'valueExplained',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'monthlyInteractionScheduling',
      title: <p>Monthly Interaction Scheduling</p>,
      type: 'radio',
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'First monthly call scheduled (date)',
          value: 'nextMonthlyCheckInScheduled',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'input-date',
              required: false,
              name: 'nextMonthlyCheckInScheduledDate',
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Patient declined scheduling',
          value: 'patientDeclinedScheduling',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'closingAdditionalQuestions',
      type: 'radio',
      title: <p>Closing - Additional Questions</p>,
      description: (
        <ScriptWrapper>
          "Before we disconnect, do you have any additional questions for me
          today?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Had no additional questions',
          value: 'noAdditionalQuestions',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: <ScriptWrapper>"Great — thank you."</ScriptWrapper>,
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Asked questions — addressed during call',
          value: 'hasAdditionalQuestions',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>"Does that answer your question?"</ScriptWrapper>
              ),
            },
          ],
        },
      ],
    },
    {
      key: makeid(10),
      name: 'closingUnderstanding',
      type: 'radio',
      title: <p>Closing - Understanding</p>,
      description: (
        <ScriptWrapper>
          "Just to confirm, do you feel you understand everything we discussed
          today?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Verbalized full understanding',
          value: 'full',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: <ScriptWrapper>"Perfect."</ScriptWrapper>,
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Verbalized partial understanding',
          value: 'partial',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "Thank you for letting me know. Is there anything I can
                  clarify before we finish?"
                </ScriptWrapper>
              ),
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Unable to verbalize understanding',
          value: 'unable',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "Thank you — I'll document that and we can follow up as
                  needed."
                </ScriptWrapper>
              ),
            },
          ],
        },
      ],
    },
    {
      key: makeid(10),
      name: '_empty',
      type: 'empty',
      title: <p>Final close</p>,
      description: (
        <ScriptWrapper>
          "Thank you for your time today. Please feel free to reach out if you
          need additional support. Take care."
        </ScriptWrapper>
      ),
    },
  ] as const satisfies TFormTemplateItem[];
};

export type TTransitionTemplateValues = ExtractStepValues<
  ReturnType<typeof createTransitionTemplate>
>;

export const createTransitionBlurb = (
  data: TTransitionTemplateValues,
  ctx: BlurbContext,
) => {
  const {
    greetingComplianceHipaaVerification,
    readingAdherenceBarrierCheck,
    readingAdherenceCheckOther,
    barriersListOther,
    barriersList,
    addressImmediateNeed,
    addressImmediateNeedIssue,
    insuranceVerificationOnFile,
    cellPhoneVerification,
    textingEnrollment,
    monthlyInteractionRequirement,
    monthlyInteractionScheduling,
    nextMonthlyCheckInScheduledDate,
    closingAdditionalQuestions,
    closingUnderstanding,
  } = data;
  const txt: string[] = [];

  if (
    greetingComplianceHipaaVerification?.length &&
    !hasField(ctx, 'greetingComplianceHipaaVerification')
  ) {
    addField(ctx, 'greetingComplianceHipaaVerification');
    const map = {
      patientIdentityVerified:
        'Greeting and compliance completed. Patient identity verified via name and date of birth.',
      recordedLineNotificationProvided:
        'Patient notified call may be recorded for quality and compliance purposes.',
      caregiverPresentConsentConfirmed:
        'Caregiver participation confirmed and patient consent obtained.',
    };
    txt.push(...mapValuesToText(greetingComplianceHipaaVerification, map));
  }

  if (
    readingAdherenceBarrierCheck &&
    !hasField(ctx, 'readingAdherenceBarrierCheck')
  ) {
    addField(ctx, 'readingAdherenceBarrierCheck');
    const map = {
      consistentReadings: 'consistent readings',
      inconsistentReadings: 'inconsistent readings',
      missedReadings: 'missed readings',
      noReadings: 'no readings',
      barriersIdentified: 'barriers identified',
      other: readingAdherenceCheckOther,
    };

    const value = map[readingAdherenceBarrierCheck];

    if (value) {
      txt.push(`Reading adherence reviewed. Patient reports ${value}.`);
    }

    if (
      readingAdherenceBarrierCheck === 'barriersIdentified' &&
      barriersList?.length
    ) {
      const barriersMap = {
        device: 'device issues',
        supplies: 'supply availability',
        schedule: 'schedule conflicts',
        education: 'education needs',
        connectivity: 'connectivity issues',
        powerCharging: 'power or charging issues',
        other: barriersListOther,
      };
      const items = mapValuesToText(barriersList, barriersMap);
      if (items.length) {
        txt.push(
          `Barriers identified and documented, including ${formatList(items)}.`,
        );
      }
    }
  }

  if (addressImmediateNeed && !hasField(ctx, 'addressImmediateNeed')) {
    addField(ctx, 'addressImmediateNeed');
    const map = {
      resolved: 'resolved',
      supportProvided: 'supported',
      escalated: 'escalated',
    };
    txt.push(`Patient concern addressed.`);
    if (addressImmediateNeedIssue?.trim()) {
      txt.push(addressImmediateNeedIssue);
    }
    txt.push(`Outcome ${map[addressImmediateNeed]}.`);
  }

  if (
    insuranceVerificationOnFile &&
    !hasField(ctx, 'insuranceVerificationOnFile')
  ) {
    addField(ctx, 'insuranceVerificationOnFile');
    const map = {
      medicare: 'Medicare',
      medicareAdvantage: 'Medicare advantage',
      commercial: 'Commercial',
      insuranceConfirmedInChart: 'Insurance confirmed in chart',
    };
    txt.push(
      `Insurance type confirmed as ${map[insuranceVerificationOnFile]} based on information on file.`,
    );
  }

  if (cellPhoneVerification && !hasField(ctx, 'cellPhoneVerification')) {
    addField(ctx, 'cellPhoneVerification');
    const map = {
      cellPhoneNumberConfirmed: 'confirmed',
      cellPhoneNumberUpdated: 'updated',
      patientReportsNoCellPhone: 'patient reports no cell phone',
    };
    txt.push(
      `Cell phone contact verified with patient ${map[cellPhoneVerification]}.`,
    );
  }

  if (textingEnrollment && !hasField(ctx, 'textingEnrollment')) {
    addField(ctx, 'textingEnrollment');
    const map = {
      alreadyEnrolled: 'already enrolled',
      offeredAndAcceptedEnrolled: 'offered and accepted',
      offeredAndDeclined: 'offered and declined',
      notEligibleNoCellPhone: 'not eligible—no cell phone',
    };
    txt.push(`Texting enrollment reviewed ${map[textingEnrollment]}.`);
  }

  if (
    monthlyInteractionRequirement?.length &&
    !hasField(ctx, 'monthlyInteractionRequirement')
  ) {
    addField(ctx, 'monthlyInteractionRequirement');
    const map = {
      requirementReviewed: 'monthly phone call requirement',
      valueExplained: 'program monitoring process explained',
    };
    const items = mapValuesToText(monthlyInteractionRequirement, map);
    if (items.length) {
      txt.push(
        `Monthly interaction requirement reviewed with patient, including ${formatList(
          items,
        )}.`,
      );
    }
  }

  if (
    monthlyInteractionScheduling &&
    !hasField(ctx, 'monthlyInteractionScheduling')
  ) {
    addField(ctx, 'monthlyInteractionScheduling');
    const map = {
      nextMonthlyCheckInScheduled: 'scheduled',
      patientDeclinedScheduling: 'not scheduled—patient declined',
    };
    let val = map[monthlyInteractionScheduling];
    if (
      monthlyInteractionScheduling === 'nextMonthlyCheckInScheduled' &&
      nextMonthlyCheckInScheduledDate
    ) {
      val += `in ${DateFormatter({
        date: nextMonthlyCheckInScheduledDate,
      })}`;
    }

    txt.push(`First monthly check-in ${val}.`);
  }

  if (
    closingAdditionalQuestions &&
    !hasField(ctx, 'closingAdditionalQuestions')
  ) {
    addField(ctx, 'closingAdditionalQuestions');

    const map = {
      noAdditionalQuestions: 'had no additional questions',
      hasAdditionalQuestions:
        'asked questions which were addressed during the call',
    };

    txt.push(
      `Call concluded after confirming patient ${map[closingAdditionalQuestions]}.`,
    );
  }

  if (closingUnderstanding && !hasField(ctx, 'closingUnderstanding')) {
    addField(ctx, 'closingUnderstanding');

    const map = {
      full: 'verbalized full understanding',
      partial: 'verbalized partial understanding',
      unable: 'was unable to verbalize understanding',
    };

    txt.push(
      `Patient ${map[closingUnderstanding]} of the information discussed.`,
    );
  }

  return txt.join(' ');
};
